import { useCallback, useMemo } from "react";
import { IEmailWithTeam, useValidateEmails } from "./useValidateEmails";

import { isValidEmail } from "utils";

interface IuseMultiEmail {
  currentTeamId: number;
  limit: number;
  userList: string[];
  emailsEntered?: (value: number) => void;
}

export const useMultiEmail = ({
  currentTeamId,
  limit,
  userList = [],
  emailsEntered,
}: IuseMultiEmail) => {
  const { emailsMap, setEmailsMap, validateEmails, ...emailValidationRest } =
    useValidateEmails();

  const emailsList = useMemo(() => Array.from(emailsMap.values()), [emailsMap]);
  const emailsWithNoTeamId = useMemo(() => {
    return emailsList.filter(({ teamId }) => !teamId);
  }, [emailsList]);
  const [isLimitExceeded, exceededMailCount] = useMemo(() => {
    return [
      !limit || emailsWithNoTeamId.length > limit,
      Math.max(emailsWithNoTeamId.length - limit, 0),
    ];
  }, [emailsWithNoTeamId.length, limit]);

  const emailsGroupedBystatus = useMemo(() => {
    return emailsList.reduce(
      (grouped, email) => {
        grouped[email.status].push(email);
        return grouped;
      },
      {
        success: [] as IEmailWithTeam[],
        pending: [] as IEmailWithTeam[],
        error: [] as IEmailWithTeam[],
      },
    );
  }, [emailsList]);

  const emailChipExists = useCallback(
    (email: string) => emailsMap.has(email),
    [emailsMap],
  );
  const emailExistsInList = useCallback(
    (email: string) => userList.includes(email),
    [userList],
  );

  const addEmails = useCallback(
    (emailsToAdd: string[]) => {
      const newEmailsMap = new Map(emailsMap);
      const newEmails = emailsToAdd
        .map(email => {
          email = email.trim().toLowerCase();
          const alreadyExists = emailExistsInList(email);
          return {
            email,
            status: alreadyExists ? "success" : "pending",
            teamId: alreadyExists ? currentTeamId : null,
          } satisfies IEmailWithTeam;
        })
        .filter(({ email }) => isValidEmail(email) && !emailChipExists(email));

      if (newEmails.length) {
        newEmails.forEach(item => newEmailsMap.set(item.email, item));
        setEmailsMap(newEmailsMap);
        validateEmails(Array.from(newEmailsMap.values()));
        emailsEntered?.(userList.length + newEmailsMap.size);
      }
    },
    [
      emailsMap,
      userList.length,
      currentTeamId,
      emailChipExists,
      emailExistsInList,
      emailsEntered,
    ],
  );

  const removeEmail = useCallback(
    (email: string) => {
      const newEmailsMap = new Map(emailsMap);
      newEmailsMap.delete(email);
      setEmailsMap(newEmailsMap);
      emailsEntered?.(userList.length + newEmailsMap.size);
    },
    [emailsMap, emailsEntered],
  );

  return {
    emailsMap,
    emailsList,
    isLimitExceeded,
    exceededMailCount,
    emailValidationRest,
    emailsGroupedBystatus,
    emailsWithNoTeamId,
    addEmails,
    removeEmail,
    setEmailsMap,
    validateEmails,
  };
};
